import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AxiosRequestConfig as Config, AxiosResponse as Response } from 'axios';
import Cookies from 'quasar/src/plugins/Cookies.js';import Notify from 'quasar/src/plugins/Notify.js';;
import client from '@/api/configs/client';
// eslint-disable-next-line import/no-cycle
import useClearAllData from '@/hooks/useClearAllData';
import { t } from '@/boot/i18n';

export class HttpService {
  constructor() {
    client.interceptors.request.use(
      this.useRequest.bind(this),
      this.handleError.bind(this),
    );

    client.interceptors.response.use(
      this.useResponse.bind(this),
      this.handleError.bind(this),
    );
  }

  private useRequest(config: Config): Config {
    const lang = (Cookies.has('locale') ? Cookies.get('locale') : 'ru') as 'ru'|'mn'|'es';

    return {
      ...config,
      headers: {
        ...config.headers,
        'Accept-Language': lang,
      },
    };
  }

  private useResponse(response: Response): Response {
    return response;
  }

  private handleError(e) {
    if (e.response == null) {
      Notify.create({
        type: 'negative',
        message: t('httpErrors.noAccess'),
      });
    } else {
      switch (e.response.status) {
        case 400: {
          Notify.create({
            type: 'negative',
            message: e.response.data?.error || e.response.data?.message || e.response.data,
          });
          break;
        }
        case 401: {
          useClearAllData().clearAllData();
          document.location.href = '/login';
          break;
        }
        case 403: {
          Notify.create({
            type: 'negative',
            message: t('httpErrors.accessDenied'),
          });
          break;
        }
        case 404: {
          Notify.create({
            type: 'negative',
            message: t('httpErrors.error404'),
          });
          break;
        }
        case 422: {
          Notify.create({
            type: 'negative',
            message: t('httpErrors.fieldFillingError'),
          });
          break;
        }
        case 429: {
          Notify.create({
            type: 'negative',
            message: t('httpErrors.manyRequests'),
          });
          break;
        }
        case 500: {
          Notify.create({
            type: 'negative',
            message: e.response?.data?.message || t('httpErrors.unexpectedErrorOccurred'),
          });
          break;
        }
        default: {
          Notify.create({
            type: 'negative',
            message: t('httpErrors.defaultError'),
          });
        }
      }
    }
    return Promise.reject(e);
  }

  private observableFactory<T = any>(
    promise: (...args) => Promise<Response<T>>,
    ...args: any[]
  ): Observable<Response<T>> {
    return new Observable<Response<T>>((observer) => from(promise(...args)).subscribe(observer));
  }

  private getObservableResponse<T = any>(
    promise: (...args) => Promise<Response<T>>,
    ...args: any[]
  ): Observable<T> {
    return this.observableFactory<T>(promise, ...args).pipe(this.transformResponse());
  }

  private transformResponse<T = any>() {
    return map<Response<T>, T>(({ data }) => {
      if (typeof data === 'object' && 'success' in data) {
        // @ts-ignore
        return data.data;
      }
      return data;
    });
  }

  get<T>(url: string, config?: Config): Observable<T> {
    return this.getObservableResponse<T>(client.get, url, config);
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: Config,
  ): Observable<T> {
    return this.getObservableResponse<T>(client.post, url, data, config);
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: Config,
  ): Observable<T> {
    return this.getObservableResponse<T>(client.put, url, data, config);
  }

  patch<T = any>(
    url: string,
    data?: any,
    config?: Config,
  ): Observable<T> {
    return this.getObservableResponse<T>(client.patch, url, data, config);
  }

  delete(url: string, config?: Config): Observable<any> {
    return this.getObservableResponse(client.delete, url, config);
  }
}

const httpService = new HttpService();

export default httpService;
